import {
  AggregateQuery,
  ClassificationType,
  ComputedQuery,
  ExtractionQuery,
  ExtractionType,
  LLMExtractionQuery,
  ParsingType,
  PipelineConfig,
  PredefinedExtraction,
  PredefinedQuery,
  RegexExtractionQuery,
  RegexMatch,
  ValidationSeverity,
} from "../../../interfaces/doc";
import { PipelineConfigForm } from "../../../schemas/OrionConfig";

export const defaultConfig = () =>
  ({
    split: null,
    classification: null,
    extraction: null,
    validation: null,
    workflows: null,
    synthesis: null,
  }) as PipelineConfigForm["config"];

export const defaultSplit = () =>
  ({
    segmentation: {
      classes: [{ name: "" }, { name: "" }],
    },
    group: {
      ghost_types: [],
      weak_types: [],
    },
  }) as PipelineConfigForm["config"]["split"];

export const defaultClassification = () =>
  ({
    max_page: 5,
    queries: [defaultClassificationQuery()],
  }) as PipelineConfigForm["config"]["classification"];

export const defaultClassificationQuery = () => ({
  name: "",
  type: ClassificationType.LLM,
  classes: [{ name: "" }, { name: "" }],
});

export const defaultClassificationRoutedQuery = () => ({
  queries: [],
  routes: [],
});

export const defaultLLMValidationRule = () => ({
  name: "",
  check: "",
  severity: ValidationSeverity.ERROR,
  success_message: "",
  error_message: "",
  untreatable_message: "",
});

export const defaultJsonataValidationRule = () => ({
  name: "",
  expression: "",
  severity: ValidationSeverity.ERROR,
  success_message: "",
  error_message: "",
  untreatable_message: "",
});

export const defaultLLMExtractionQuery = () =>
  ({
    name: "",
    type: ExtractionType.LLM,
    query: "",
    parsing_type: ParsingType.String,
    many: false,
    enum_values: null,
    custom_schema: null,
    expected_value: null,
  }) as LLMExtractionQuery;

export const defaultRegexExtractionQuery = () =>
  ({
    name: "",
    type: ExtractionType.REGEX,
    pattern: "",
    params: { match: RegexMatch.FIRST, group_number: null },
    parsing_type: ParsingType.String,
    many: false,
    enum_values: null,
    custom_schema: null,
    expected_value: null,
  }) as RegexExtractionQuery;

export const defaultPredefinedExtractionQuery = () =>
  ({
    name: "",
    type: ExtractionType.PREDEFINED,
    query: PredefinedExtraction.EPC,
    expected_value: null,
  }) as PredefinedQuery;

export const defaultComputedExtractionQuery = () =>
  ({
    name: "",
    type: ExtractionType.COMPUTED,
    expression: "",
    parsing_type: ParsingType.String,
  }) as ComputedQuery;

export const defaultAggregateExtractionQuery = () =>
  ({
    name: "",
    type: ExtractionType.AGGREGATE,
    expression: "",
    parsing_type: ParsingType.String,
  }) as AggregateQuery;

export const defaultExtractionRoutedQuery = () => ({
  queries: [defaultLLMExtractionQuery()],
  routes: [],
});

export const defaultExtraction = () =>
  ({
    params: { chunk_size: 3 },
    queries: [defaultLLMExtractionQuery()],
  }) as PipelineConfigForm["config"]["extraction"];

const completeQuery = (query: ExtractionQuery) => {
  switch (query.type) {
    case ExtractionType.LLM:
      return { ...defaultLLMExtractionQuery(), ...query };

    case ExtractionType.REGEX:
      return { ...defaultRegexExtractionQuery(), ...query };

    case ExtractionType.COMPUTED:
      return { ...defaultComputedExtractionQuery(), ...query };

    case ExtractionType.AGGREGATE:
      return { ...defaultAggregateExtractionQuery(), ...query };

    case ExtractionType.PREDEFINED:
      return { ...defaultPredefinedExtractionQuery(), ...query };
  }
};

export const defaultWorflowState = () => ({
  name: "",
  is_default: false,
  set_on_validation_success: false,
  set_on_validation_failure: false,
  set_on_validation_error: false,
});

export const completeConfig = (config: PipelineConfig) => {
  return {
    ...config,
    extraction: !!config.extraction
      ? {
          ...config.extraction,
          queries: config.extraction.queries.map((query) => {
            if ("queries" in query) {
              return {
                ...query,
                queries: query.queries.map((q) => completeQuery(q)),
              };
            } else
              return {
                ...query,
                ...completeQuery(query),
              };
          }),
        }
      : config.extraction,
  };
};
